/*
 * Authors: mariushirjanu
 * Created: 05/04/2023
 */
import _ from 'underscore';
import { util } from '@app/shared/util';
import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { OrderEntryWebshopService } from '../webshop/order-entry-webshop.service';
import { IContact, ILocationExtended, IObject } from '@type_definitions/datatypes';
import { OrderEntryLocationService } from './order-entry-location.service';
import { IInvoiceAddress, ILocationOption, InvoiceAddressType, InvoiceContactType, IOrderEntryContact, IShippingAddress, LocationDataType, LocationProperty, OrderEntryAddressType, OrderEntryAddressTypeExtra, OrderEntryContactType, OrderEntryContactTypeExtra, OrderEntryLocationDataService, ShippingContactType } from './order-entry-location-data.service';
import { OrderEntryShippingService } from './shipping/order-entry-shipping.service';
import { OrderEntryInvoiceService } from './invoice/order-entry-invoice.service';
import { GaDeferredService, IGADeferred } from '@app/shared/services/ga-deferred.service';
import { OrderEntryRequesterService } from '../requester/order-entry-requester.service';
import { IAddressData, IContactModel, IContactOption } from './base-order-entry-location-to.service';
import { BILLINGTYPE_CENTRAL } from '@app/shared/services/billing/billing-data.service';
import { GaValidateService, IValidatingComponent } from '@app/shared/formly/ga-validate.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { GaFormlyValidatorService } from '@app/shared/formly/ga-formly-validator.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { OrderEntryService } from '../order-entry.service';

interface IContactTypeModel {
  type:IContactOption;
}

@Component({
  selector: 'order-location-contact',
  template: `
    <div [ngClass]="this.dataType.toLowerCase() + '-contact'" [hidden]="loading">
      <h2 translate>{{ title }}</h2>
      <div class="row">
        <div class="col-xsm-6 col-sm-4 col-md-3">
          <formly [form]="contactTypeForm" [fields]="contactTypeFields" [model]="contactTypeModel" class="form-horizontal contact-type-chooser"></formly>
        </div>
        <div class="col-xsm-6 col-sm-8 col-md-9">
          <div class="contact-form">
            <formly [form]="contactForm" [fields]="contactFields" [model]="selectedContact" class="form-horizontal"></formly>
          </div>
        </div>
      </div>
    </div>
  `
})
export class OrderLocationContactComponent implements IValidatingComponent, OnInit, OnChanges, AfterViewInit {
  @Input() public title:string;
  @Input() public locationToModel:OrderEntryShippingService|OrderEntryInvoiceService;
  @Input() public otherLocationToModel:OrderEntryShippingService|OrderEntryInvoiceService;
  @Input() public dataType:LocationDataType;
  @Input() public addressType:InvoiceAddressType;
  @Input() public location:ILocationExtended;
  @Input() public fixedList:boolean;
  @Input() public billingType:string;
  @Output() public onLoadContactDone:EventEmitter<void> = new EventEmitter<void>();

  public contactTypeForm:UntypedFormGroup = new UntypedFormGroup({});
  public contactTypeModel:IContactTypeModel;
  public contactTypeFields:IExtraFieldConfigurationObject[];
  public contactFields:IExtraFieldConfigurationObject[];
  public contactForm:UntypedFormGroup = new UntypedFormGroup({});

  private contactTypeField:IExtraFieldConfigurationObject;
  private loadedContact:IContact;
  public loading:boolean;
  private recentContactField:IExtraFieldConfigurationObject;
  private onValidationReadyDeferred:IGADeferred<void>;
  private locationOption:ILocationOption;
  private selectedAddress:IAddressData;
  private loadedAddress:IShippingAddress | IInvoiceAddress;

  constructor(
    private formlyService:GaFormlyService,
    deferredService:GaDeferredService,
    private orderEntryLocationDataService:OrderEntryLocationDataService,
    private orderEntryLocationService:OrderEntryLocationService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private orderEntryRequesterService:OrderEntryRequesterService,
    private orderEntryService:OrderEntryService,
    private validateService:GaValidateService,
    private translationService:TranslationService,
    private formlyValidatorService:GaFormlyValidatorService,
    private formattingService:FormattingService,
    private changeDetectorRef:ChangeDetectorRef
  ) {
    this.onValidationReadyDeferred = deferredService.defer();
  }

  public ngOnInit():void {
    this.contactTypeModel = <any>{};
    this.initContactForm();
    this.contactTypeFields = this.createContactTypeFields();

    const locationProp:LocationProperty = this.orderEntryLocationDataService.locationProperty(this.dataType);
    this.loadedContact = this.orderEntryService.order?.[ locationProp ]?.contact;

    this.fillContactTypeField();
  }

  public ngAfterViewInit():void {
    this.onValidationReadyDeferred.resolve();
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes.billingType) {
      this.billingType = changes.billingType.currentValue;
    }

    if (changes.addressType) {
      this.addressType = changes.addressType.currentValue;
      this.selectedAddress = this.locationToModel.getAddress(this.addressType);
      this.loadedAddress = this.locationToModel.getLoadedAddress(this.addressType);
      this.updateContactType(<any>this.selectedAddress);
      this.onSelectedAddressChange();
    }

    if (changes.location) {
      this.location = changes.location.currentValue;
      this.locationOption = this.locationToModel.getLocationOption(this.location?.id);
      this.onSelectedAddressChange();
      this.selectedContact = this.orderEntryLocationService.formatContact(this.locationOption?.contact);
    }
  }

  private fillContactTypeField():Promise<IOrderEntryContact[] | void> {
    // fill in contact type field
    if (this.contactTypeField) {
      // get options from server
      const requesterId:number = this.orderEntryRequesterService.contact?.id;
      const selectedShopId:number = this.orderEntryWebshopService.selectedShop?.id;
      const selectedCountryId:number = this.orderEntryWebshopService.selectedCountry?.id;
      const billingType:string = this.billingType || this.orderEntryWebshopService.shopContext?.billingType;

      if (selectedShopId && selectedCountryId) {
        this.loading = true;
        return this.formlyService.fillField(
          this.contactTypeField,
          this.orderEntryLocationDataService.contacts(this.dataType, requesterId, selectedShopId, selectedCountryId, billingType)
            .then((contactData:IOrderEntryContact[]) => {
              // !!! setContactTypeOptions also updates contacts data to model (if any)
              this.locationToModel.setContactTypeOptions(contactData, !!this.loadedContact, this.orderEntryService.requesterRequired);
              this.setContactTypeModel();

              // for existing location the contact model is set later after the list of locations is loaded & location model set (see changes.selectedAddress in onChanges method)
              if (this.addressType !== OrderEntryAddressType.EXISTING_LOCATION) {
                this.initContactModels();
                this.initContactForm();
              }

              return this.locationToModel.contactTypeOptions;
            })
        ).finally(() => {
          this.loading = false;
          this.onLoadContactDone.emit();
        });
      }
    }

    return Promise.resolve();
  }

  private fillRecentContactField():void {
    const selectedShopId:number = this.orderEntryWebshopService.selectedShop?.id;
    const selectedCountryId:number = this.orderEntryWebshopService.selectedCountry?.id;
    const testMode:boolean = this.orderEntryWebshopService.testMode;

    if (selectedShopId && selectedCountryId) {
      this.formlyService.fillField(this.recentContactField, this.orderEntryLocationDataService.recentContacts(this.dataType, selectedShopId, selectedCountryId, testMode)
        .then((recentContacts:IContact[]) => {
          if (!this.selectedContact?.contact && recentContacts?.length) {
            this.selectedContact = this.selectedContact || <any>{};
            // when there is already a selected recent contact, use it; otherwise fallback to the first contact from the list
            this.selectedContact.contact = _.clone(this.locationToModel.selectedContact?.contact?.id ? this.locationToModel.selectedContact.contact : recentContacts[ 0 ]);
            this.contactForm.patchValue({ contact: this.selectedContact.contact });
          }
          return recentContacts;
        })
      );
    }
  }

  private setContactTypeModel():void {
    let selectedOption:IContactOption = this.locationToModel.contactType || _.findWhere(this.locationToModel.contactTypeOptions, { default: true });

    // set default contact type of address when there is a contact type and contact on address
    const defaultAddress:IInvoiceAddress = <IInvoiceAddress>this.locationToModel.getLoadedAddress(OrderEntryAddressTypeExtra.DEFAULT_INVOICE_ADDRESS);
    if (!selectedOption && defaultAddress?.invoiceToContact && defaultAddress.contact) {
      selectedOption = _.find(this.locationToModel.contactTypeOptions, { id: defaultAddress.invoiceToContact });
    }

    //use the one associated with the selected address
    if (!selectedOption) {
      this.updateContactType(this.loadedAddress);
      selectedOption = this.contactTypeModel.type;
    }

    //fallback to the first one
    if (!selectedOption) {
      const options:IContactOption[] = this.locationToModel.contactTypeOptions;
      selectedOption = options[ 0 ];
    }

    this.contactTypeModel.type = selectedOption;
    this.contactTypeForm.patchValue({ type: selectedOption });
  }

  private initContactModels(forceCopy?:boolean):void {
    // when selected contact has been already set, do not fetch it again from contact data object (server)
    // this is only the case when initialising for the first time when visiting Shipping/Invoice tab
    if(_.isEmpty(this.selectedContact)) {

      _.forEach(this.orderEntryLocationDataService.CONTACT_TYPES, (typeId:InvoiceContactType | ShippingContactType) => {
        let contactModel:IContactModel = <IContactModel>{};
        if (typeId === this.locationToModel.contactType?.id && this.locationToModel.contactData) { // for the selected contact type -> set contact model to the contact data loaded from the server
          contactModel = this.locationToModel.contactData || <IContactModel>{};
        }

        switch (typeId) {
          case OrderEntryContactType.CURRENT_CONTACT:
            contactModel.contact = this.loadedContact;
            break;
          case OrderEntryContactType.DEFAULT_CONTACT:
            contactModel.contact = this.locationOption?.contact || this.loadedAddress?.contact;
            break;
          case OrderEntryContactType.MY_CONTACT:
            contactModel = this.orderEntryLocationService.formatContact(this.orderEntryRequesterService.contact);
            break;
          case OrderEntryContactTypeExtra.SAME_AS_SHIPPING_CONTACT:
            contactModel = _.clone(this.otherLocationToModel.selectedContact) || <IContactModel>{}; // point to the shipping contact
            break;
          default:
            break;
        }

        this.updateContactData(contactModel, forceCopy);
        this.locationToModel.setContact(typeId, contactModel);
      });

    }
  }

  private createContactTypeFields():IExtraFieldConfigurationObject[] {
    return [
      this.contactTypeField = this.formlyService.aRadio('type', 'ORDERENTRY.LOCATION.CONTACT_TYPE')
        .isRequired()
        .withFormat('type')
        .withoutWrapper()
        .onChange(this.onContactTypeFieldChange)
        .build()
    ];
  }

  private createContactFields():IExtraFieldConfigurationObject[] {
    const readOnly:Function = ():boolean => {
      if(this.contactTypeModel.type?.id === OrderEntryContactType.CURRENT_CONTACT || this.contactTypeModel.type?.id === OrderEntryContactType.DEFAULT_CONTACT){
        return !this.allowAddEditContact();
      }
      return;
    };

    const fullName:string = this.selectedContact?.fullName;
    const email:string = this.selectedContact?.email;
    const directPhone:string = this.selectedContact?.directPhone;
    const contactInformationCompleted:boolean = !!(fullName && (this.dataType === LocationDataType.INVOICE || email) && directPhone);

    const labelClass:string = 'col-sm-2';
    const valueClass:string = 'col-sm-9';

    const contactFields:IExtraFieldConfigurationObject[] = [];

    contactFields.push(
      this.recentContactField = this.formlyService.aSelect('contact', 'ORDERENTRY.LOCATION.ADDRESS.RECENT')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat('fullName')
        .withExtraOptions({
          templateOptions: {
            addInitialOption: false,
            trackBy: 'fullName'
          }
        })
        .onChange(this.onRecentContactChange)
        .withHideExpression(() => !this.isRecentContact())
        .build(),

      this.formlyService.anInput('fullName', 'GA.GENERAL.NAME')
        .withMaxLength(100)
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .onChange(this.onContactChange)
        .withHideExpression(() => !(!readOnly() || (readOnly() && !fullName)))
        .build(),

      this.formlyService.aValue('fullName', 'GA.GENERAL.NAME')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withHideExpression(() => !(readOnly() && fullName && !contactInformationCompleted))
        .build(),

      this.formlyService.anInput('email', 'GA.GENERAL.EMAIL')
        .withMaxLength(200)
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withInfo(this.translationService.instant('GA.INFO.MULTIPLE_EMAILS'))
        .withExtraOptions({
          validators: {
            email: this.formlyValidatorService.multipleEmailValidator
          }
        })
        .onChange(this.onContactChange)
        .withHideExpression(() => this.dataType !== LocationDataType.SHIPPING || !(!readOnly() || (readOnly() && !email)))
        .build(),

      this.formlyService.aValue('email', 'GA.GENERAL.EMAIL')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withHideExpression(() => this.dataType !== LocationDataType.SHIPPING || !(readOnly() && email && !contactInformationCompleted))
        .build(),

      this.formlyService.anInput('directPhone', 'GA.GENERAL.PHONE')
        .withMaxLength(60)
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .onChange(this.onContactChange)
        .withHideExpression(() => !(!readOnly() || (readOnly() && !directPhone)))
        .build(),

      this.formlyService.aValue('directPhone', 'GA.GENERAL.PHONE')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withHideExpression(() => !(readOnly() && directPhone && !contactInformationCompleted))
        .build()
    );

    // read-only fields
    contactFields.push(
      this.formlyService.aValue('fullName', 'GA.GENERAL.CONTACT')
        .withFormat(() => {
          let contact:IContact;
          if(this.selectedContact?.contact && this.dataType === LocationDataType.INVOICE){
            contact = (<IContact>{
              fullName: this.selectedContact.contact.fullName,
              directPhone: this.selectedContact.contact.directPhone
            });
          } else {
            contact = this.selectedContact?.contact;
          }
          return this.formattingService.formatContactHtml(contact);
        })
        .withLabelClass('col-md-2')
        .withValueClass('col-md-4')
        .withHideExpression(() => !(readOnly() && contactInformationCompleted))
        .build()
    );

    return contactFields;
  }

  private onContactTypeFieldChange = (contactType?:IContactOption):void => {
    contactType = contactType || this.contactTypeModel.type;

    if (contactType?.id) {
      if(this.isRecentContact()){
        this.fillRecentContactField();
      }

      this.updateContactData(this.selectedContact);
      this.locationToModel.contactType = contactType;

      // also save selected contact to locationToModel when contact type changes
      if(!_.isEmpty(this.selectedContact)){
        this.locationToModel.selectedContact = _.clone(this.selectedContact);
      }
    }
  };

  private onContactChange = ():void => {
    if(!this.selectedContact){
      this.selectedContact = <any>{};
    }
    this.selectedContact = _.clone(this.selectedContact);
  };

  private onRecentContactChange = ():void => {
    this.updateContactData(this.selectedContact, true);
    this.locationToModel.selectedContact = _.clone(this.selectedContact); // save selected contact to locationToModel when recent contact changes
  };

  private updateContactType(newAddress:ILocationOption|IInvoiceAddress|IShippingAddress):void {
    if (newAddress) {
      const contactTypeId:InvoiceContactType | ShippingContactType = (this.dataType === LocationDataType.SHIPPING)? (<IShippingAddress>newAddress).shipToContact : (<IInvoiceAddress>newAddress).invoiceToContact;
      if (contactTypeId) {
        const newContactType:IContactOption = _.findWhere(this.locationToModel.contactTypeOptions, { id: contactTypeId });
        this.contactTypeField.formControl.setValue(newContactType);
        this.locationToModel.contactType = newContactType;
      }
    }
  }

  private updateContactData(contactModel:IContactModel, forceUpdate?:boolean):void {
    if (!contactModel || !contactModel.contact) {
      return;
    }

    const dataToUpdate:IObject = {};

    if (forceUpdate || !contactModel.fullName) {
      contactModel.fullName = contactModel.contact.fullName;
      dataToUpdate.fullName = contactModel.contact.fullName;
    }
    if (forceUpdate || !contactModel.email) {
      contactModel.email = contactModel.contact.email;
      dataToUpdate.email = contactModel.contact.email;
    }
    if (forceUpdate || !contactModel.directPhone) {
      contactModel.directPhone = contactModel.contact.directPhone;
      dataToUpdate.directPhone = contactModel.contact.directPhone;
    }

    // reactive form update
    if (dataToUpdate) {
      this.contactForm.patchValue({ ...dataToUpdate });
    }
  }

  private allowAddEditContact():boolean {
    return !this.fixedList && !(this.dataType === LocationDataType.INVOICE && this.billingType === BILLINGTYPE_CENTRAL); // the same as allowAddEditLocation()
  }

  private onSelectedAddressChange():void {
    this.disableEnableDefaultContactOption();

    // use the contact from the loaded address when there is not yet a selected contact
    if(this.loadedAddress?.contact && util.isNullOrUndefinedOrEmpty(this.selectedContact?.contact)){
      this.selectedContact = this.orderEntryLocationService.formatContact(this.loadedAddress?.contact);
    }

    if(this.location) {
      this.updateContactType(this.locationOption);
      this.initContactModels();
    }
  }

  private disableEnableDefaultContactOption():void {
    const contact:IContact = this.loadedAddress?.contact;

    //disable / enable if there is (no) default contact associated with the selected address
    const defaultContactOptions:IContactOption = _.findWhere(this.locationToModel.contactTypeOptions, { id: OrderEntryContactType.DEFAULT_CONTACT });
    if(defaultContactOptions) {
      defaultContactOptions.disabled = !contact;
    }
  }

  public beforeValidate = ():Promise<any> => {
    return this.onValidationReadyDeferred.promise;
  };

  public validate = ():Promise<void|string[]> => {
    this.copyContactDataFromOtherModel();
    return this.validateService.validateFields(this.contactFields, this.selectedContact, true);
  };

  private copyContactDataFromOtherModel():void {
    if (this.selectedContact?.fullName && this.otherLocationToModel?.selectedContact?.fullName && this.otherLocationToModel.selectedContact.fullName === this.selectedContact.fullName) {
      const dataToCopy:IObject = {};
      if (!this.selectedContact.email && this.dataType !== LocationDataType.INVOICE) {
        this.selectedContact.email = this.otherLocationToModel.selectedContact.email;
        dataToCopy.email = this.otherLocationToModel.selectedContact.email;
      }
      if (!this.selectedContact.directPhone) {
        this.selectedContact.directPhone = this.otherLocationToModel.selectedContact.directPhone;
        dataToCopy.directPhone = this.otherLocationToModel.selectedContact.directPhone;
      }

      this.contactForm.patchValue({ ...dataToCopy });
    }
  }

  // define getter & setter for contactModel for tracking the changes
  public get selectedContact():IContactModel {
    return this.locationToModel.selectedContact;
  }

  public set selectedContact(contactModel:IContactModel) {
    if (contactModel) {
      this.locationToModel.selectedContact = _.clone(contactModel);
      this.changeDetectorRef.detectChanges();
    }
  }

  private isRecentContact():boolean {
    return this.contactTypeModel?.type?.id === OrderEntryContactTypeExtra.RECENT_INVOICE_CONTACTS || this.contactTypeModel?.type?.id === OrderEntryContactTypeExtra.RECENT_SHIP_CONTACTS;
  }

  private initContactForm():void {
    this.contactFields = this.createContactFields();
  }
}